import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { Partner, PartnerPricing, Association } from '@/types/partner';
import AdminLayout from '@/components/admin/AdminLayout';
import { FetchPartnerResult } from '@/types/admin';
import ImageUpload from '@/components/admin/ImageUpload';

interface ManagePartnersProps {
  type: 'Career Counselor' | 'Domain Expert' | 'Psychologist';
}

const ManagePartners: React.FC<ManagePartnersProps> = ({ type }) => {
  const { id } = useParams<{ id: string }>();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [isEditing, setIsEditing] = useState(!!id);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    education: '',
    experience: 0,
    about_us: '',
    status: 'active' as 'active' | 'inactive',
    min_session_time: 30,
    session_charge: 0,
    profile_image: ''
  });
  const [associationId, setAssociationId] = useState<string | null>(null);

  useEffect(() => {
    fetchAssociationId();
  }, [type]);

  useEffect(() => {
    if (id && associationId) {
      fetchPartnerDetails(id);
    }
  }, [id, associationId]);

  const fetchPartnerDetails = async (partnerId: string) => {
    try {
      setDataLoading(true);
      
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .eq('id', partnerId)
        .single();
      
      if (error) throw error;
      
      if (!data) {
        toast.error('Partner not found');
        return;
      }

      const { data: pricingData, error: pricingError } = await supabase
        .from('partner_pricing')
        .select('*')
        .eq('partner_id', partnerId)
        .eq('association_id', associationId)
        .single();
      
      if (pricingError && pricingError.code !== 'PGRST116') {
        throw pricingError;
      }
      
      const partner = data as Partner;
      setSelectedPartner(partner);
      
      setFormData({
        name: partner.name || '',
        email: partner.email || '',
        phone: partner.phone || '',
        education: partner.education || '',
        experience: partner.experience || 0,
        about_us: partner.about_us || '',
        status: partner.status as 'active' | 'inactive',
        min_session_time: pricingData?.min_session_time || 30,
        session_charge: pricingData?.session_charge || 0,
        profile_image: partner.profile_image || ''
      });
      
      setIsEditing(true);
    } catch (error) {
      console.error(`Error fetching ${type} details:`, error);
      toast.error(`Failed to load ${type.toLowerCase()} details`);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchAssociationId = async () => {
    try {
      const { data, error } = await supabase
        .from('associations')
        .select('id')
        .eq('name', type)
        .single();

      if (error) throw error;
      setAssociationId(data.id);
    } catch (error) {
      console.error('Error fetching association:', error);
      toast.error('Failed to load association information');
    }
  };

  const fetchPartners = async () => {
    try {
      setLoading(true);
      
      const { data: associationData, error: associationError } = await supabase
        .from('associations')
        .select('id')
        .eq('name', type)
        .single();
      
      if (associationError) throw associationError;
      
      const { data: partnerAssociations, error: partnerError } = await supabase
        .from('partner_associations')
        .select('partner_id')
        .eq('association_id', associationData.id);
      
      if (partnerError) throw partnerError;
      
      if (partnerAssociations.length === 0) {
        setPartners([]);
        setLoading(false);
        return;
      }
      
      const partnerIds = partnerAssociations.map(pa => pa.partner_id);
      
      const { data: partnersData, error: partnersError } = await supabase
        .from('partners')
        .select('*')
        .in('id', partnerIds);
      
      if (partnersError) throw partnersError;
      
      setPartners(partnersData as Partner[]);
    } catch (error) {
      console.error(`Error fetching ${type}s:`, error);
      toast.error(`Failed to load ${type.toLowerCase()}s`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'experience' || name === 'min_session_time' || name === 'session_charge') {
      setFormData({
        ...formData,
        [name]: type === 'number' ? Number(value) : value
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleImageUploaded = (url: string) => {
    setFormData({
      ...formData,
      profile_image: url
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      education: '',
      experience: 0,
      about_us: '',
      status: 'active',
      min_session_time: 30,
      session_charge: 0,
      profile_image: ''
    });
    setIsEditing(false);
    setSelectedPartner(null);
  };

  const handleEdit = async (partner: Partner) => {
    try {
      setSelectedPartner(partner);
      
      const { data: pricingData, error: pricingError } = await supabase
        .from('partner_pricing')
        .select('*')
        .eq('partner_id', partner.id)
        .eq('association_id', associationId)
        .single();
      
      if (pricingError && pricingError.code !== 'PGRST116') {
        throw pricingError;
      }
      
      setFormData({
        name: partner.name,
        email: partner.email,
        phone: partner.phone || '',
        education: partner.education || '',
        experience: partner.experience || 0,
        about_us: partner.about_us || '',
        status: partner.status,
        min_session_time: pricingData?.min_session_time || 30,
        session_charge: pricingData?.session_charge || 0,
        profile_image: partner.profile_image || ''
      });
      
      setIsEditing(true);
    } catch (error) {
      console.error(`Error preparing ${type} for edit:`, error);
      toast.error('Could not load partner data for editing');
    }
  };

  const handleDelete = async (partner: Partner) => {
    if (!window.confirm(`Are you sure you want to delete ${partner.name}?`)) {
      return;
    }

    try {
      const { error: assocError } = await supabase
        .from('partner_associations')
        .delete()
        .eq('partner_id', partner.id);
      
      if (assocError) throw assocError;
      
      const { error: pricingError } = await supabase
        .from('partner_pricing')
        .delete()
        .eq('partner_id', partner.id);
      
      if (pricingError) throw pricingError;
      
      const { error: partnerError } = await supabase
        .from('partners')
        .delete()
        .eq('id', partner.id);
      
      if (partnerError) throw partnerError;
      
      toast.success(`${type} deleted successfully`);
      fetchPartners();
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      toast.error('Failed to delete partner');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!associationId) {
      toast.error('Association not found');
      return;
    }

    try {
      const partnerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        education: formData.education,
        experience: Number(formData.experience),
        about_us: formData.about_us,
        status: formData.status,
        profile_image: formData.profile_image
      };

      if (isEditing && selectedPartner) {
        const { error: updateError } = await supabase
          .from('partners')
          .update(partnerData)
          .eq('id', selectedPartner.id);
        
        if (updateError) throw updateError;

        const { data: existingPricing, error: pricingCheckError } = await supabase
          .from('partner_pricing')
          .select('id')
          .eq('partner_id', selectedPartner.id)
          .eq('association_id', associationId)
          .single();

        if (pricingCheckError && pricingCheckError.code !== 'PGRST116') {
          throw pricingCheckError;
        }

        const pricingData = {
          partner_id: selectedPartner.id,
          association_id: associationId,
          min_session_time: Number(formData.min_session_time),
          session_charge: Number(formData.session_charge),
          currency: 'INR'
        };

        if (existingPricing) {
          const { error: pricingUpdateError } = await supabase
            .from('partner_pricing')
            .update(pricingData)
            .eq('id', existingPricing.id);
          
          if (pricingUpdateError) throw pricingUpdateError;
        } else {
          const { error: pricingInsertError } = await supabase
            .from('partner_pricing')
            .insert(pricingData);
          
          if (pricingInsertError) throw pricingInsertError;
        }

        toast.success(`${type} updated successfully`);
      } else {
        const { data: newPartner, error: insertError } = await supabase
          .from('partners')
          .insert(partnerData)
          .select()
          .single();
        
        if (insertError) throw insertError;

        const { error: assocError } = await supabase
          .from('partner_associations')
          .insert({
            partner_id: newPartner.id,
            association_id: associationId
          });
        
        if (assocError) throw assocError;

        const { error: pricingError } = await supabase
          .from('partner_pricing')
          .insert({
            partner_id: newPartner.id,
            association_id: associationId,
            min_session_time: Number(formData.min_session_time),
            session_charge: Number(formData.session_charge),
            currency: 'INR'
          });
        
        if (pricingError) throw pricingError;

        toast.success(`${type} created successfully`);
      }

      resetForm();
      fetchPartners();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} ${type}:`, error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} ${type.toLowerCase()}`);
    }
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>{isEditing ? `Edit ${type}` : `Add New ${type}`} | Admin</title>
      </Helmet>
      
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? `Edit ${type}` : `Add New ${type}`}
          </h1>
        </div>
        
        {dataLoading ? (
          <div className="bg-white p-6 rounded-md shadow flex justify-center items-center">
            <div className="space-y-4 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
              <p>Loading {type} details...</p>
            </div>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-md shadow">
            <h2 className="text-xl font-semibold mb-4">{isEditing ? `Edit ${type}` : `Add New ${type}`}</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                  <input
                    type="text"
                    name="education"
                    value={formData.education}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Experience (years)</label>
                  <input
                    type="number"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Session Time (minutes)</label>
                  <input
                    type="number"
                    name="min_session_time"
                    value={formData.min_session_time}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="15"
                    step="15"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session Charge (INR)</label>
                  <input
                    type="number"
                    name="session_charge"
                    value={formData.session_charge}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    min="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profile Image</label>
                <ImageUpload 
                  onImageUploaded={handleImageUploaded} 
                  currentImageUrl={formData.profile_image}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">About</label>
                <textarea
                  name="about_us"
                  value={formData.about_us}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={4}
                />
              </div>
              
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-blue text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {isEditing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManagePartners;
